import { businessFiltersAtom } from "@/app/(saas)/(app)/businesses/_components/filters/state";
import { useNearbyListings } from "@/app/(saas)/(app)/businesses/_hooks/use-nearby-listings";
import { useRelaxedListings } from "@/app/(saas)/(app)/businesses/_hooks/use-relaxed-listings";
import { NotifyWaitlistSheet } from "@/app/(saas)/(app)/businesses/_components/notify-waitlist-sheet";
import { Button } from "@/components/ui/button";
import { H2 } from "@/components/ui/heading";
import { useAtom } from "jotai";
import { useTranslations } from "next-intl";
import { type RefObject, useMemo } from "react";
import { valuesWithAppName } from "@/lib/i18n";
import { Icon } from "@/components/ui/icon";
import Link from "next/link";
import type { MapClient } from "@repo/maps";
import {
  formatLocation,
} from "@/lib/locations";
import { ListingPreview } from "@/components/listings/listing-preview";
import { buildListingUrl } from "@/lib/url";
import slugify from "slugify";
import { MapPinIcon, ArrowRightIcon } from "lucide-react";

export type BusinessListZeroDataStateProps = {
  mapRef: RefObject<MapClient | null>;
};

export function BusinessListZeroDataState({
  mapRef,
}: BusinessListZeroDataStateProps) {
  const t = useTranslations();

  const [filters, setFilters] = useAtom(businessFiltersAtom);

  const hasBusinessTypeFilters = useMemo(() => {
    return filters.businessTypeIds.length > 0;
  }, [filters]);

  const hasStatusesFilters = useMemo(() => {
    return filters.statuses.length > 0;
  }, [filters]);

  const hasFinancialsFilters = useMemo(() => {
    return [
      filters.priceRange,
      filters.revenueRange,
      filters.profitRange,
      filters.sdeRange,
      filters.sdeMultipleRange,
      filters.revenueMultipleRange,
      filters.downPaymentRange,
    ].some((range) => range.min !== null || range.max !== null)
      || !filters.includeMissingMultiples;
  }, [filters]);

  const hasDetailsFilters = useMemo(() => {
    return (
      [
        filters.establishmentAgeRange,
        filters.employeeCountRange,
        filters.ownerWorkedHoursRange,
      ].some((range) => range.min !== null || range.max !== null) ||
      filters.propertyIncluded
    );
  }, [filters]);

  const hasFinancingFilters = useMemo(() => {
    return (
      filters.financingAvailable ||
      filters.sbaPrequalified ||
      filters.visaQualified
    );
  }, [filters]);

  const hasAnyFilters = useMemo(() => {
    return Boolean(
      hasBusinessTypeFilters ||
      hasStatusesFilters ||
      hasFinancialsFilters ||
      hasDetailsFilters ||
      hasFinancingFilters
    );
  }, [
    hasBusinessTypeFilters,
    hasStatusesFilters,
    hasFinancialsFilters,
    hasDetailsFilters,
    hasFinancingFilters,
  ]);

  const {
    listings: displayListings,
    isNearby: hasNearbyListings,
    isLoading: isListingsLoading,
  } = useNearbyListings({ enabled: !hasAnyFilters });

  const {
    listings: relaxedListings,
    isLoading: isRelaxedLoading,
  } = useRelaxedListings({ enabled: hasAnyFilters });

  const locationName = useMemo(() => {
    if (!filters.location) return null;
    return formatLocation(filters.location);
  }, [filters.location]);

  const regionLinks = useMemo(() => {
    const links: Array<{ label: string; href: string }> = [];
    const location = filters.location;
    if (!location) return links;

    const { country, region } = location.context;
    const countryCode = country?.code?.toLowerCase();

    if (region && countryCode) {
      links.push({
        label: t("saas.search.zds.noFilters.exploreRegion", {
          region: region.name,
        }),
        href: `/businesses/${countryCode}/${slugify(region.name, { lower: true })}`,
      });
    }

    if (country && countryCode) {
      links.push({
        label: t("saas.search.zds.noFilters.exploreCountry", {
          country: country.name,
        }),
        href: `/businesses/${countryCode}`,
      });
    }

    return links;
  }, [filters.location, t]);

  function clearBusinessTypeFilters() {
    setFilters((prev) => ({
      ...prev,
      businessTypeIds: [],
    }));
  }

  function clearStatusesFilters() {
    setFilters((prev) => ({
      ...prev,
      statuses: [],
    }));
  }

  function clearFinancialsFilters() {
    setFilters((prev) => ({
      ...prev,
      priceRange: { min: null, max: null },
      revenueRange: { min: null, max: null },
      profitRange: { min: null, max: null },
      sdeRange: { min: null, max: null },
      sdeMultipleRange: { min: null, max: null },
      revenueMultipleRange: { min: null, max: null },
      downPaymentRange: { min: null, max: null },
      includeMissingMultiples: true,
    }));
  }

  function clearDetailsFilters() {
    setFilters((prev) => ({
      ...prev,
      establishmentAgeRange: { min: null, max: null },
      employeeCountRange: { min: null, max: null },
      ownerWorkedHoursRange: { min: null, max: null },
      propertyIncluded: null,
    }));
  }

  function clearFinancingFilters() {
    setFilters((prev) => ({
      ...prev,
      financingAvailable: false,
      sbaPrequalified: false,
      visaQualified: false,
    }));
  }

  function clearAllFilters() {
    setFilters((prev) => ({
      ...prev,
      businessTypeIds: [],
      statuses: [],
      priceRange: { min: null, max: null },
      revenueRange: { min: null, max: null },
      profitRange: { min: null, max: null },
      sdeRange: { min: null, max: null },
      sdeMultipleRange: { min: null, max: null },
      revenueMultipleRange: { min: null, max: null },
      downPaymentRange: { min: null, max: null },
      includeMissingMultiples: true,
      establishmentAgeRange: { min: null, max: null },
      employeeCountRange: { min: null, max: null },
      ownerWorkedHoursRange: { min: null, max: null },
      propertyIncluded: null,
      financingAvailable: false,
      sbaPrequalified: false,
      visaQualified: false,
    }));
  }

  if (hasAnyFilters) {
    return (
      <div className="flex flex-col gap-6">
        <div className="rounded-xl border border-border/80 bg-card/90 p-5 shadow-[0_1px_0_rgba(255,255,255,0.92)_inset,0_8px_24px_rgba(0,0,0,0.08)] backdrop-blur-md supports-[backdrop-filter]:bg-card/72">
          <div className="mb-4 flex items-start gap-3">
            <div className="mt-0.5 rounded-full bg-muted p-2">
              <Icon icon="Search" className="h-4 w-4 text-muted-foreground" />
            </div>
            <div>
              <H2>{t("saas.search.zds.withFilters.title")}</H2>
              <p className="mt-1 text-sm text-muted-foreground">
                {t("saas.search.zds.withFilters.description")}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {hasBusinessTypeFilters && (
              <Button variant="outline" size="sm" onClick={clearBusinessTypeFilters}>
                {t("saas.search.zds.withFilters.clear.businessType")}
              </Button>
            )}

            {hasStatusesFilters && (
              <Button variant="outline" size="sm" onClick={clearStatusesFilters}>
                {t("saas.search.zds.withFilters.clear.statuses")}
              </Button>
            )}

            {hasFinancialsFilters && (
              <Button variant="outline" size="sm" onClick={clearFinancialsFilters}>
                {t("saas.search.zds.withFilters.clear.financials")}
              </Button>
            )}

            {hasDetailsFilters && (
              <Button variant="outline" size="sm" onClick={clearDetailsFilters}>
                {t("saas.search.zds.withFilters.clear.details")}
              </Button>
            )}

            {hasFinancingFilters && (
              <Button variant="outline" size="sm" onClick={clearFinancingFilters}>
                {t("saas.search.zds.withFilters.clear.financing")}
              </Button>
            )}

            <Button variant="outline" size="sm" onClick={clearAllFilters}>
              {t("saas.search.zds.withFilters.clear.all")}
            </Button>
          </div>
        </div>

        {/* Relaxed filter suggestions */}
        {isRelaxedLoading && (
          <div className="space-y-3">
            <div className="h-5 w-48 animate-pulse rounded bg-muted" />
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              {[0, 1, 2].map((i) => (
                <div
                  key={`rskel-${i}`}
                  className="h-[280px] animate-pulse rounded-xl border border-border bg-secondary"
                />
              ))}
            </div>
          </div>
        )}

        {!isRelaxedLoading && relaxedListings.length > 0 && (
          <div className="space-y-3">
            <div>
              <h3 className="text-sm font-semibold text-foreground">
                {t("saas.search.zds.withFilters.relaxed.title")}
              </h3>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {t("saas.search.zds.withFilters.relaxed.subtitle")}
              </p>
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              {relaxedListings.map((listing) => (
                <a
                  key={listing.slug ?? `listing-${listing.id}`}
                  href={buildListingUrl(listing)}
                  className="flex flex-col rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground"
                  target="_blank"
                  rel="noreferrer"
                >
                  <ListingPreview listing={listing} />
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="rounded-xl border border-border/80 bg-card/90 p-5 shadow-[0_1px_0_rgba(255,255,255,0.92)_inset,0_8px_24px_rgba(0,0,0,0.08)] backdrop-blur-md supports-[backdrop-filter]:bg-card/72">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 shrink-0 rounded-full bg-muted p-2">
            <MapPinIcon className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="flex-1">
            <H2>
              {locationName
                ? t("saas.search.zds.noFilters.title", {
                    location: locationName,
                  })
                : t("saas.search.zds.noFilters.titleFallback")}
            </H2>
            <p className="mt-1 text-sm text-muted-foreground">
              {hasNearbyListings || isListingsLoading
                ? t("saas.search.zds.noFilters.subtitle")
                : t("saas.search.zds.noFilters.subtitleNoNearby")}
            </p>

            <div className="mt-3 flex flex-wrap items-center gap-2">
              <NotifyWaitlistSheet mapRef={mapRef} />

              {regionLinks.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="inline-flex items-center gap-1.5 rounded-full border border-border bg-secondary px-3.5 py-1.5 text-[13px] font-medium text-foreground transition-all duration-200 hover:-translate-y-px hover:border-border hover:shadow-[0_4px_12px_rgba(0,0,0,0.06)]"
                >
                  <MapPinIcon className="h-3.5 w-3.5 text-muted-foreground" />
                  {link.label}
                  <ArrowRightIcon className="h-3 w-3 text-muted-foreground" />
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Nearby or Featured Listings */}
      {isListingsLoading && (
        <div className="space-y-3">
          <div className="h-5 w-48 animate-pulse rounded bg-muted" />
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {[0, 1, 2].map((i) => (
              <div
                key={`skel-${i}`}
                className="h-[280px] animate-pulse rounded-xl border border-border bg-secondary"
              />
            ))}
          </div>
        </div>
      )}

      {!isListingsLoading && displayListings.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-foreground">
            {hasNearbyListings
              ? t("saas.search.zds.noFilters.nearbyTitle")
              : t(
                  "saas.search.zds.noFilters.featuredTitle",
                  valuesWithAppName(t, {}),
                )}
          </h3>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {displayListings.map((listing) => (
              <a
                key={listing.slug ?? `listing-${listing.id}`}
                href={buildListingUrl(listing)}
                className="flex flex-col rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground"
                target="_blank"
                rel="noreferrer"
              >
                <ListingPreview listing={listing} />
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

BusinessListZeroDataState.displayName = "BusinessListZeroDataState";
